const PLAIN_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const cloneDate = (date) => new Date(date.getTime());

export const isPlainDateString = (value) => {
  if (typeof value !== 'string') return false;
  return PLAIN_DATE_REGEX.test(value.trim());
};

export const ensureDate = (value) => {
  if (!value && value !== 0) return null;

  if (value instanceof Date) {
    return cloneDate(value);
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    // Heuristic: timestamps under 1e12 are likely seconds
    return value < 1e12 ? new Date(value * 1000) : new Date(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (isPlainDateString(trimmed)) {
      const [year, month, day] = trimmed.split('-').map(Number);
      if ([year, month, day].every(Number.isFinite)) {
        return new Date(year, month - 1, day);
      }
      return null;
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
  }

  return null;
};

export const normalizeDateForVisibility = (value) => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.includes('T')) {
      const [datePart] = trimmed.split('T');
      if (isPlainDateString(datePart)) {
        return ensureDate(datePart);
      }
    }
  }
  return ensureDate(value);
};

export const formatDateTime = (
  value,
  {
    locale = 'en-US',
    includeTime = true,
    options = {},
  } = {},
) => {
  const date = ensureDate(value);
  if (!date) return 'TBD';

  const baseOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  };

  const formatOptions = includeTime
    ? { hour: '2-digit', minute: '2-digit', ...baseOptions }
    : baseOptions;

  return date.toLocaleDateString(locale, formatOptions);
};

export const formatDateOnly = (value, { locale = 'en-US', options = {} } = {}) =>
  formatDateTime(value, { locale, includeTime: false, options });

export const toIsoDateString = (value) => {
  const date = ensureDate(value);
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const addHours = (value, hours) => {
  const date = ensureDate(value);
  if (!date) return null;
  return new Date(date.getTime() + hours * 60 * 60 * 1000);
};

export const differenceInMilliseconds = (endValue, startValue) => {
  const end = ensureDate(endValue);
  const start = ensureDate(startValue);
  if (!end || !start) return NaN;
  return end.getTime() - start.getTime();
};

export const calculateEarlyAccessDate = (startDate, hours = 48) =>
  toIsoDateString(addHours(startDate, -hours));

/** Hours after public start before prize media is revealed to non-subscribers. */
export const CLASSIFIED_PUBLIC_HOURS = 48;

/**
 * True until the public start date, when the giveaway opens to free and paid users.
 */
export const isBeforePublicGiveawayStart = (giveaway, now = new Date()) => {
  const startDate = normalizeDateForVisibility(giveaway?.startDate);
  if (!startDate || !Number.isFinite(startDate.getTime())) return false;
  const nowDate = ensureDate(now) || new Date();
  return nowDate < startDate;
};

/**
 * When the classified overlay lifts for public viewers — 48 hours after start.
 * Uses ensureDate so timed start values stay precise; date-only strings resolve
 * to local midnight.
 */
export const getClassifiedRevealDate = (giveaway, hours = CLASSIFIED_PUBLIC_HOURS) => {
  const start = ensureDate(giveaway?.startDate);
  if (!start) return null;
  return addHours(start, hours);
};

/**
 * True while non-subscribers should still see the classified overlay on a live
 * giveaway (from public start until start + 48 hours).
 */
export const isWithinClassifiedPublicWindow = (giveaway, now = new Date()) => {
  const publicStart = ensureDate(giveaway?.startDate);
  const revealAt = getClassifiedRevealDate(giveaway);
  if (!publicStart || !revealAt) return false;

  const nowDate = ensureDate(now) || new Date();
  return nowDate >= publicStart && nowDate < revealAt;
};

/**
 * Classified overlay rules:
 * - Paid subscribers and admins never see it
 * - Past / sold-out / completed giveaways never show it (full images for everyone)
 * - Signed-out visitors see it only during the first 48 hours after the
 *   giveaway's public start date; logged-in free accounts and everyone else
 *   see full prize media once the giveaway is publicly live
 * - After that 48-hour window, active giveaways show full prize media for all
 *   viewers, like past giveaways
 */
export const shouldShowClassifiedPrize = (giveaway, {
  isSubscriber = false,
  isAdmin = false,
  isPast = false,
  isClosed = false,
  isAuthenticated = false,
  now = new Date(),
} = {}) => {
  if (isSubscriber || isAdmin || isPast || isClosed || isAuthenticated) return false;
  return isWithinClassifiedPublicWindow(giveaway, now);
};

